import './App.css';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import PublicLayout from "./layouts/PublicLayout";
import ScrollToTop from "./components/ScrollToTop";
import { initLenis, destroyLenis } from "./utils/useLenis";

import Home from "./pages/user/Home";
import AboutPage from "./pages/user/AboutPage";
import AgriPage from "./pages/user/AgriPage";
import SurveyPage from "./pages/user/SurveyPage";
import CameraPage from "./pages/user/CameraPage";
import OverviewPage from "./pages/user/OverviewPage";
import SupportPage from "./pages/user/SupportPage";
import FranchisePage from "./pages/user/FranchisePage";
import PartnerPage from "./pages/user/PartnerPage";
import FlytutorPage from "./pages/user/FlytutorPage";
import FlyhubPage from "./pages/user/FlyhubPage";
import AirspacemapPage from "./pages/user/AirspacemappPage";
import AgriSimPage from "./pages/user/AgriSimPage";
import AgriSimPlayerPage from "./pages/user/AgriSimPlayerPage";
import FCPage from "./pages/user/FCPage";
import DesignPage from "./pages/user/DesignPage";
import PrivacyPolicy from "./pages/user/PrivacyPolicyPage";
import TermsAndConditions from "./pages/user/TermsAndConditionsPage";

import StoreHomepage from "./pages/user/Store/StoreHomepage";
import ProductListingPage from "./pages/user/Store/ProductListingPage";
import ProductDetailsPage from "./pages/user/Store/ProductDetailsPage";

import NotFound from "./pages/user/NotFound";

// Wrapper to handle redirection for paths containing literal asterisks
function NotFoundRedirect() {
  const location = useLocation();
  if (location.pathname.includes('*')) {
    return <Navigate to="/" replace />;
  }
  return <NotFound />;
}

function App() {
  // Disable browser scroll restoration
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Initialize Lenis and handle resize
  useEffect(() => {
    initLenis();

    let resizeTimer;
    let lastWidth = window.innerWidth;

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      if (currentWidth === lastWidth) return; // ignore height-only changes (mobile address bar)
      lastWidth = currentWidth;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        destroyLenis();
        initLenis(false); // false = don't scroll to top on resize
      }, 300);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
      destroyLenis();
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>

        {/* PUBLIC WEBSITE */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />

          <Route path="/about" element={<AboutPage />} />

          {/* STORE */}
          <Route path="/store" element={<StoreHomepage />} />
          <Route path="/store/products" element={<ProductListingPage />} />
          <Route path="/store/products/:slug" element={<ProductDetailsPage />} />

          {/* DroneCommanderGCS variant pages */}
          <Route path="/our-businesses/agri" element={<AgriPage />} />
          <Route path="/our-businesses/survey" element={<SurveyPage />} />
          <Route path="/our-businesses/camera" element={<CameraPage />} />
          <Route path="/our-businesses/overview" element={<OverviewPage />} />

          {/* Redirects for legacy/alternate paths */}
          <Route path="/our-solutions/agri" element={<Navigate to="/our-businesses/agri" replace />} />
          <Route path="/our-solutions/survey" element={<Navigate to="/our-businesses/survey" replace />} />
          <Route path="/our-solutions/camera" element={<Navigate to="/our-businesses/camera" replace />} />
          <Route path="/our-solutions/overview" element={<Navigate to="/our-businesses/overview" replace />} />
          <Route path="/our-solutions" element={<Navigate to="/our-businesses/overview" replace />} />
          <Route path="/our-solutions/*" element={<Navigate to="/our-businesses/overview" replace />} />
          <Route path="/mapping-drone" element={<Navigate to="/our-businesses/survey" replace />} />
          <Route path="/mapping-drone/*" element={<Navigate to="/our-businesses/survey" replace />} />
          <Route path="/training-drone" element={<Navigate to="/businesses/flytutor" replace />} />
          <Route path="/training-drone/*" element={<Navigate to="/businesses/flytutor" replace />} />
          <Route path="/home-2" element={<Navigate to="/" replace />} />
          <Route path="/home-2/*" element={<Navigate to="/" replace />} />
          <Route path="/app-development" element={<Navigate to="/" replace />} />
          <Route path="/app-development/*" element={<Navigate to="/" replace />} />
          <Route path="/about-company" element={<Navigate to="/about" replace />} />
          <Route path="/about-company/*" element={<Navigate to="/about" replace />} />
          <Route path="/about-us" element={<Navigate to="/about" replace />} />
          <Route path="/about-us/*" element={<Navigate to="/about" replace />} />
          <Route path="/contacts" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contacts/*" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact/*" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact-us" element={<Navigate to="/contact/support" replace />} />
          <Route path="/contact-us/*" element={<Navigate to="/contact/support" replace />} />
          <Route path="/wp-content/*" element={<Navigate to="/" replace />} />
          <Route path="/wp-admin/*" element={<Navigate to="/" replace />} />
          <Route path="/wp-includes/*" element={<Navigate to="/" replace />} />
          <Route path="/wp-login.php" element={<Navigate to="/" replace />} />
          <Route path="/wp-signup.php" element={<Navigate to="/" replace />} />
          <Route path="/wp-cron.php" element={<Navigate to="/" replace />} />
          <Route path="/xmlrpc.php" element={<Navigate to="/" replace />} />
          {/* Platform  */}
          <Route path="/businesses/flytutor" element={<FlytutorPage />} />
          <Route path="/businesses/flyhub" element={<FlyhubPage />} />
          <Route path="/businesses/airspacemap" element={<AirspacemapPage />} />
          <Route path="/businesses/agri-sim" element={<AgriSimPage />} />
          <Route path="/player" element={<AgriSimPlayerPage />} />
          {/*Hardware*/}
          <Route path="/hardware/fc" element={<FCPage />} />
          {/*Design*/}
          <Route path="/design/airframe" element={<DesignPage />} />
          {/* Contact pages */}
          <Route path="/contact/support" element={<SupportPage />} />
          <Route path="/contact/franchise" element={<FranchisePage />} />
          <Route path="/contact/partners" element={<PartnerPage />} />

          {/* Redirect parent categories to their main overview or home */}
          <Route path="/our-businesses" element={<Navigate to="/our-businesses/overview" replace />} />
          <Route path="/businesses" element={<Navigate to="/" replace />} />

          {/*Legal*/}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

          {/* Catch-all for non-existing routes */}
          <Route path="*" element={<NotFoundRedirect />} />
        </Route>

      </Routes>
    </Router>
  );
}

export default App;